import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  MessageSquare,
  Phone,
  AtSign,
  Footprints,
  Mail,
  MailQuestion,
  Wallet,
  Calendar,
  ScrollText,
  Eye,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  CalendarPlus,
  Send,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import type {
  ConversationEntry,
  CustomerRecord,
  Deposit,
} from '@/domain/types';
import { simTimeFull, simTimeShort, aud } from '@/utils/format';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import PhotoGallery from '@/components/PhotoGallery';
import {
  simulateDepositLinkViewed,
  simulateStripeWebhookPaid,
} from '@/domain/depositFlow';
import { sendWaiver, simulateWaiverSigned } from '@/domain/waiverFlow';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';

const channelIcon = {
  'meta-dm': MessageSquare,
  'ig-dm': AtSign,
  sms: MessageSquare,
  voice: Phone,
  'web-form': MailQuestion,
  email: Mail,
  'walk-in': Footprints,
} as const;

interface Props {
  entry: ConversationEntry;
  customer?: CustomerRecord;
}

export default function ConversationTimelineEntry({ entry, customer }: Props) {
  // Dedicated lifecycle cards render in their own components so their hooks —
  // and the standard-message hooks below — are never called after an early
  // return (react-hooks/rules-of-hooks).
  if (entry.depositId) {
    return <DepositLifecycleCard entry={entry} customer={customer} />;
  }
  if (entry.waiverId) {
    return <WaiverLifecycleCard entry={entry} customer={customer} />;
  }
  return <MessageTimelineEntry entry={entry} customer={customer} />;
}

function MessageTimelineEntry({ entry }: Props) {
  const Icon = channelIcon[entry.channel] ?? MessageSquare;
  const outbound = entry.direction === 'out';
  return (
    <article
      className={clsx(
        'rounded-lg border px-4 py-3 mb-3',
        outbound
          ? 'bg-ink-deep border-gold-dim/30'
          : 'bg-ink-soft/50 border-cream-soft/15',
      )}
      data-testid={`timeline-entry-${entry.id}`}
    >
      <header className="flex items-center justify-between text-[11px] font-inter text-cream-soft tracking-widest uppercase">
        <span className="flex items-center gap-2">
          <Icon size={12} />
          {entry.channel}
          {entry.authorName ? <span className="text-gold">· {entry.authorName}</span> : null}
        </span>
        <time dateTime={entry.timestamp}>{simTimeFull(entry.timestamp)}</time>
      </header>
      <p className="font-serif text-base text-cream mt-2 leading-relaxed">{entry.body}</p>

      {entry.transcript && entry.transcript.length > 0 ? (
        <ol
          className="mt-3 space-y-1.5 border-l border-gold-dim/30 pl-3"
          data-testid={`entry-transcript-${entry.id}`}
        >
          {entry.transcript.map((line, i) => (
            <li key={i} className="flex gap-3 text-sm">
              <span
                className={clsx(
                  'shrink-0 font-inter text-[10px] uppercase tracking-widest pt-0.5',
                  line.speaker === 'customer' ? 'text-gold-bright' : 'text-cream-soft',
                )}
              >
                {line.speaker}
              </span>
              <span className="font-serif text-cream leading-relaxed">{line.body}</span>
            </li>
          ))}
        </ol>
      ) : null}

      {entry.attachments && entry.attachments.length > 0 ? (
        <div className="mt-3 space-y-2">
          {entry.attachments.some((att) => att.kind === 'image') ? (
            <PhotoGallery
              photos={entry.attachments
                .filter((att) => att.kind === 'image')
                .map((att) => ({ url: att.url }))}
              size="medium"
              density="compact"
              testid={`entry-images-${entry.id}`}
              maxVisible={6}
            />
          ) : null}
          <div className="flex gap-2 flex-wrap">
            {entry.attachments
              .filter((att) => att.kind !== 'image')
              .map((att, i) =>
                att.kind === 'audio' ? (
                  <span key={i} className="pill-cream text-[10px]">
                    Audio · {att.durationSec ?? 0}s
                  </span>
                ) : att.url.startsWith('data:text/calendar') ? (
                  <a
                    key={i}
                    href={att.url}
                    download="tattoo-session.ics"
                    className="pill-cream text-[10px] inline-flex items-center gap-1 hover:bg-gold-bright/10 transition"
                    data-testid={`entry-ics-${entry.id}`}
                  >
                    <CalendarPlus size={10} /> Add to calendar (.ics)
                  </a>
                ) : (
                  <span key={i} className="pill-cream text-[10px]">
                    {att.kind}
                  </span>
                ),
              )}
          </div>
        </div>
      ) : null}
      {entry.futureReadinessSignal ? (
        <p className="mt-3 font-inter text-[11px] text-gold uppercase tracking-widest">
          Future-readiness captured: {entry.futureReadinessSignal.rawPhrase}
        </p>
      ) : null}
    </article>
  );
}

type DepositStatus = 'sent' | 'viewed' | 'paid' | 'placed' | 'expired';

function depositStatus(d: Deposit): DepositStatus {
  if (d.status === 'expired') return 'expired';
  if (d.status === 'paid' && d.bookingId) return 'placed';
  if (d.status === 'paid') return 'paid';
  if (d.viewedAt) return 'viewed';
  return 'sent';
}

const STATUS_META: Record<
  DepositStatus,
  { label: string; tone: 'cool' | 'warn' | 'good' | 'critical'; icon: typeof Send }
> = {
  sent: { label: 'Sent', tone: 'cool', icon: Send },
  viewed: { label: 'Viewed', tone: 'warn', icon: Eye },
  paid: { label: 'Paid', tone: 'good', icon: CheckCircle2 },
  placed: { label: 'Booking placed', tone: 'good', icon: CalendarPlus },
  expired: { label: 'Expired', tone: 'critical', icon: AlertTriangle },
};

const STATUS_TONE = {
  cool: 'bg-cream-soft/10 text-cream-soft border-cream-soft/30',
  warn: 'bg-gold/15 text-gold-bright border-gold-bright/40',
  good: 'bg-mint/15 text-mint border-mint/40',
  critical: 'bg-blood/15 text-blood border-blood/40',
};

function DepositLifecycleCard({
  entry,
  customer,
}: {
  entry: ConversationEntry;
  customer?: CustomerRecord;
}) {
  const navigate = useNavigate();
  const deposit = useBrainStore((s) => (entry.depositId ? s.deposits[entry.depositId] : undefined));
  const booking = useBrainStore((s) => (deposit?.bookingId ? s.bookings[deposit.bookingId] : undefined));
  const artist = useBrainStore((s) => (booking ? s.artists[booking.artistId] : undefined));
  const pushNotification = useUIStore((s) => s.pushNotification);
  const addConvo = useBrainStore((s) => s.addConversationEntry);
  const clockIso = useSimulationStore((s) => s.clockIso);

  // Compute status up front (with a safe default when the deposit is missing)
  // so the pulse hooks below run unconditionally, before any early return
  // (react-hooks/rules-of-hooks).
  const status: DepositStatus = deposit ? depositStatus(deposit) : 'sent';

  // Pulse the card briefly when this deposit transitions to "paid" — the
  // moment the demo wants Georgina's eye drawn to.
  const prevStatusRef = useRef(status);
  const [justPaid, setJustPaid] = useState(false);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (prev !== 'paid' && status === 'paid') {
      setJustPaid(true);
      const t = setTimeout(() => setJustPaid(false), 1600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [status]);

  if (!deposit) {
    return (
      <article
        className="rounded-lg border border-gold-dim/30 bg-ink-deep px-4 py-3 mb-3"
        data-testid={`timeline-entry-${entry.id}`}
      >
        <p className="font-serif text-sm text-cream-soft">Deposit record missing.</p>
      </article>
    );
  }

  const meta = STATUS_META[status];
  const StatusIcon = meta.icon;
  const expiringSoonMs = new Date(deposit.expiresAt).getTime() - new Date(clockIso).getTime();
  const expiringSoon = status !== 'paid' && status !== 'placed' && status !== 'expired' && expiringSoonMs <= 4 * 60 * 60 * 1000;

  const followUp = () => {
    if (!customer) return;
    addConvo(customer.id, {
      id: `convo-followup-${Date.now()}`,
      customerId: customer.id,
      channel: deposit.channelSent,
      direction: 'out',
      body: `Hey ${customer.firstName}, just nudging you on the deposit link in case it got lost. Let me know if you'd like me to extend it.`,
      timestamp: clockIso,
      authorRole: 'agent',
      authorName: 'You',
    });
    pushNotification({
      intensity: 'info',
      title: 'Follow-up sent',
      body: `${customer.firstName} nudged on ${deposit.channelSent}.`,
      ttlMs: 3500,
    });
  };

  const resend = () => {
    if (!customer) return;
    simulateDepositLinkViewed(deposit.id); // simulate they opened the resent link
    setTimeout(() => simulateStripeWebhookPaid({ depositId: deposit.id }), 2000);
    pushNotification({
      intensity: 'info',
      title: 'Resent deposit link',
      body: `${customer.firstName} got a fresh link.`,
      ttlMs: 3500,
    });
  };

  const placeBooking = () => {
    if (!customer) return;
    navigate(`/calendar/${customer.primaryStudioId}`, {
      state: { placeBookingDepositId: deposit.id },
    });
  };

  return (
    <motion.article
      animate={
        justPaid
          ? {
              boxShadow: [
                '0 0 0px 0 rgba(95,191,147,0)',
                '0 0 20px 6px rgba(95,191,147,0.55)',
                '0 0 0px 0 rgba(95,191,147,0)',
              ],
              scale: [1, 1.02, 1],
            }
          : {}
      }
      transition={{ duration: 1.4, ease: 'easeOut' }}
      className={clsx(
        'rounded-lg border px-4 py-3 mb-3',
        status === 'paid' || status === 'placed'
          ? 'bg-mint/5 border-mint/40'
          : status === 'expired'
            ? 'bg-blood/5 border-blood/40'
            : 'bg-gold/5 border-gold-bright/40',
      )}
      data-testid={`timeline-entry-${entry.id}`}
    >
      <header className="flex items-center justify-between text-[11px] font-inter text-cream-soft tracking-widest uppercase">
        <span className="flex items-center gap-2">
          <Wallet size={12} />
          Deposit link · {deposit.channelSent}
        </span>
        <time dateTime={entry.timestamp}>{simTimeFull(entry.timestamp)}</time>
      </header>

      <div className="flex items-baseline gap-3 mt-2 flex-wrap">
        <span className="font-cinzel text-xl text-gold-bright">{aud(deposit.amountAud)}</span>
        <span className="font-serif text-base text-cream">
          {entry.body}
        </span>
      </div>

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <span
          className={clsx(
            'inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-inter uppercase tracking-widest',
            STATUS_TONE[meta.tone],
          )}
          data-testid={`deposit-status-${deposit.id}`}
        >
          <StatusIcon size={10} /> {meta.label}
        </span>
        {deposit.viewedAt && status !== 'placed' ? (
          <span className="font-inter text-[10px] text-cream-faint uppercase tracking-widest">
            opened {simTimeShort(deposit.viewedAt)}
          </span>
        ) : null}
        {deposit.paidAt ? (
          <span className="font-inter text-[10px] text-mint uppercase tracking-widest">
            paid {simTimeShort(deposit.paidAt)}
          </span>
        ) : null}
        {expiringSoon ? (
          <span className="font-inter text-[10px] text-blood uppercase tracking-widest">
            expires soon
          </span>
        ) : null}
      </div>

      {booking && artist ? (
        <div
          className="mt-3 rounded-md border border-mint/40 bg-mint/10 px-3 py-2"
          data-testid={`deposit-booking-summary-${deposit.id}`}
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-inter text-[10px] uppercase tracking-widest text-mint">
                Booking placed
              </p>
              <p className="font-inter text-sm text-cream mt-0.5">
                {simTimeFull(booking.start)} · {artist.displayName} · {booking.sessionLength.replace('-', ' ')}
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                navigate(`/calendar/${booking.studioId}`, {
                  state: { focusDayIso: booking.start, focusArtistId: booking.artistId },
                })
              }
              className="btn-ghost text-[11px]"
              data-testid={`deposit-open-calendar-${deposit.id}`}
            >
              <Calendar size={11} /> Open on calendar
            </button>
          </div>
          {booking.referencePhotos.length > 0 ? (
            <div className="mt-2">
              <PhotoGallery
                photos={booking.referencePhotos.map((url) => ({ url }))}
                size="thumb"
                density="compact"
                testid={`deposit-placed-thumbs-${deposit.id}`}
                maxVisible={4}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {status === 'paid' && !booking ? (
        <p
          className="mt-3 font-inter text-[10px] uppercase tracking-widest text-blood"
          data-testid={`deposit-awaiting-placement-${deposit.id}`}
        >
          Awaiting placement · no booking yet
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2 mt-3" data-testid={`deposit-actions-${deposit.id}`}>
        {status === 'sent' || status === 'viewed' ? (
          <button
            type="button"
            onClick={followUp}
            className="btn-ghost text-[11px]"
            data-testid={`deposit-follow-up-${deposit.id}`}
          >
            <Send size={11} /> Follow up
          </button>
        ) : null}
        {status === 'paid' ? (
          <button
            type="button"
            onClick={placeBooking}
            className="btn-primary text-[11px]"
            data-testid={`deposit-place-${deposit.id}`}
          >
            <CalendarPlus size={11} /> Place booking now
          </button>
        ) : null}
        {status === 'expired' ? (
          <button
            type="button"
            onClick={resend}
            className="btn-ghost text-[11px]"
            data-testid={`deposit-resend-${deposit.id}`}
          >
            <RotateCcw size={11} /> Resend
          </button>
        ) : null}
      </div>
    </motion.article>
  );
}

type WaiverCardStatus = 'not-sent' | 'sent' | 'signed' | 'expired';

const WAIVER_STATUS_META: Record<
  WaiverCardStatus,
  { label: string; tone: 'cool' | 'warn' | 'good' | 'critical'; icon: typeof Send }
> = {
  'not-sent': { label: 'Not sent', tone: 'cool', icon: ScrollText },
  sent: { label: 'Awaiting signature', tone: 'warn', icon: Send },
  signed: { label: 'Signed', tone: 'good', icon: CheckCircle2 },
  expired: { label: 'Expired', tone: 'critical', icon: AlertTriangle },
};

function WaiverLifecycleCard({
  entry,
  customer,
}: {
  entry: ConversationEntry;
  customer?: CustomerRecord;
}) {
  const waiver = useBrainStore((s) => (entry.waiverId ? s.waivers[entry.waiverId] : undefined));
  const pushNotification = useUIStore((s) => s.pushNotification);

  const status = (waiver?.status ?? 'not-sent') as WaiverCardStatus;

  // Pulse the card when the waiver transitions to "signed".
  const prevStatusRef = useRef(status);
  const [justSigned, setJustSigned] = useState(false);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (prev !== 'signed' && status === 'signed') {
      setJustSigned(true);
      const t = setTimeout(() => setJustSigned(false), 1600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [status]);

  if (!waiver) {
    return (
      <article
        className="rounded-lg border border-gold-dim/30 bg-ink-deep px-4 py-3 mb-3"
        data-testid={`timeline-entry-${entry.id}`}
      >
        <p className="font-serif text-sm text-cream-soft">Waiver record missing.</p>
      </article>
    );
  }

  const meta = WAIVER_STATUS_META[status];
  const StatusIcon = meta.icon;
  const answers = waiver.answers;
  const disclosures = answers
    ? [
        answers.allergies.length ? `Allergies: ${answers.allergies.join(', ')}` : null,
        answers.conditions.length ? `Conditions: ${answers.conditions.join(', ')}` : null,
        answers.medications.length ? `Medications: ${answers.medications.join(', ')}` : null,
      ].filter(Boolean)
    : [];

  const resend = () => {
    sendWaiver({ bookingId: waiver.bookingId, resend: true });
    pushNotification({
      intensity: 'info',
      title: 'Waiver resent',
      body: `${customer?.firstName ?? 'Customer'} got a fresh waiver link.`,
      ttlMs: 3500,
    });
  };

  const simulateSign = () => {
    const waiverId = waiver.id;
    setTimeout(() => simulateWaiverSigned(waiverId), 1400);
    pushNotification({
      intensity: 'info',
      title: 'Opening on customer device…',
      body: `${customer?.firstName ?? 'Customer'} is signing the waiver.`,
      ttlMs: 2000,
    });
  };

  return (
    <motion.article
      animate={
        justSigned
          ? {
              boxShadow: [
                '0 0 0px 0 rgba(95,191,147,0)',
                '0 0 20px 6px rgba(95,191,147,0.55)',
                '0 0 0px 0 rgba(95,191,147,0)',
              ],
              scale: [1, 1.02, 1],
            }
          : {}
      }
      transition={{ duration: 1.4, ease: 'easeOut' }}
      className={clsx(
        'rounded-lg border px-4 py-3 mb-3',
        status === 'signed'
          ? 'bg-mint/5 border-mint/40'
          : status === 'expired'
            ? 'bg-blood/5 border-blood/40'
            : 'bg-gold/5 border-gold-bright/40',
      )}
      data-testid={`timeline-entry-${entry.id}`}
    >
      <header className="flex items-center justify-between text-[11px] font-inter text-cream-soft tracking-widest uppercase">
        <span className="flex items-center gap-2">
          <ScrollText size={12} />
          Medical waiver · {waiver.sendChannel}
        </span>
        <time dateTime={entry.timestamp}>{simTimeFull(entry.timestamp)}</time>
      </header>

      <p className="font-serif text-base text-cream mt-2">{entry.body}</p>

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <span
          className={clsx(
            'inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-inter uppercase tracking-widest',
            STATUS_TONE[meta.tone],
          )}
          data-testid={`waiver-status-${waiver.id}`}
        >
          <StatusIcon size={10} /> {meta.label}
        </span>
        {waiver.signedAt ? (
          <span className="font-inter text-[10px] text-mint uppercase tracking-widest">
            signed {simTimeShort(waiver.signedAt)}
          </span>
        ) : null}
      </div>

      {status === 'signed' ? (
        <div
          className="mt-3 rounded-md border border-mint/40 bg-mint/10 px-3 py-2"
          data-testid={`waiver-disclosures-${waiver.id}`}
        >
          <p className="font-inter text-[10px] uppercase tracking-widest text-mint flex items-center gap-1">
            <ShieldCheck size={11} /> Questionnaire complete
          </p>
          {disclosures.length ? (
            <ul className="mt-1 font-serif text-sm text-cream space-y-0.5">
              {disclosures.map((d) => (
                <li key={d as string}>{d}</li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 font-serif text-sm text-cream-soft">No medical disclosures.</p>
          )}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 mt-3" data-testid={`waiver-actions-${waiver.id}`}>
        {status === 'sent' ? (
          <>
            <button
              type="button"
              onClick={simulateSign}
              className="btn-primary text-[11px]"
              data-testid={`waiver-simulate-${waiver.id}`}
            >
              <Smartphone size={11} /> Simulate signing
            </button>
            <button
              type="button"
              onClick={resend}
              className="btn-ghost text-[11px]"
              data-testid={`waiver-resend-${waiver.id}`}
            >
              <RotateCcw size={11} /> Resend
            </button>
          </>
        ) : null}
        {status === 'expired' ? (
          <button
            type="button"
            onClick={resend}
            className="btn-ghost text-[11px]"
            data-testid={`waiver-resend-${waiver.id}`}
          >
            <RotateCcw size={11} /> Resend
          </button>
        ) : null}
      </div>
    </motion.article>
  );
}
